import React, { useEffect, useState, useRef } from 'react';
import { useScreenSize } from '../../contexts/ScreenSizeContext';
import { useStyle } from '../../contexts/StyleContext';
import PageLayout from '../../components/PageLayout/PageLayout';
import LandingPageDemo from '../../components/LandingPageDemo/LandingPageDemo';
import loviLaptop from '../../assets/lovi_images/Lovi_On_Laptop.webp';
import { trackViewContent } from '../../services/metaPixel';
import { trackViewContent as trackTikTokViewContent } from '../../services/tiktokPixel';
import { navigateToSignupWithTracking } from '../../services/trackingNavigation';

const TRUST_SIGNALS = [
  'Cancel anytime',
  'Online scheduling + reminders',
  'Plans from $9.99/month',
  'No hidden fees',
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Set up your business',
    description: 'Add your services, hours, breaks, and time off.',
  },
  {
    step: '02',
    title: 'Share your booking page',
    description: 'Put your link in your Instagram bio or send it directly to clients.',
  },
  {
    step: '03',
    title: 'Manage your appointments',
    description: 'Keep bookings, changes, and availability in one place.',
  },
];

const FAQ_ITEMS = [
  {
    question: 'What kind of businesses can use LoviBook?',
    answer: 'LoviBook is built for service businesses including hair salons, barbershops, nail studios, massage therapists, personal trainers, tutors, photographers, and any independent provider who wants online booking, appointment reminders, and a clean dashboard to manage schedules, services, and client communication.',
  },
  {
    question: 'How much does it cost?',
    answer: 'LoviBook plans start at $9.99 per month. The Basic plan includes online booking, availability management, appointment reminders, and a branded booking page. Higher tiers add advanced branding tools, messaging features, and more.',
  },
  {
    question: 'Does LoviBook include appointment reminders?',
    answer: 'Yes. Every plan includes automatic email reminders to help clients stay on top of upcoming appointments.',
  },
  {
    question: 'Can my clients reschedule or cancel their own appointments?',
    answer: 'Yes. Clients receive a confirmation email with a secure manage link that lets them view, reschedule, or cancel their appointment without needing to contact you directly.',
  },
  {
    question: 'Will clients be able to book from their phones?',
    answer: 'Yes. The online booking flow is designed to work cleanly across phones, tablets, and desktop screens so clients can schedule from any device.',
  },
  {
    question: 'Are there contracts, booking fees, or commissions?',
    answer: 'No. LoviBook uses simple monthly billing with no booking fees, commissions, or long-term contract.',
  },
];

// Keyframe styles injected once
const keyframes = `@keyframes fadeSlide {0%{opacity:0;transform:translateY(18px)}100%{opacity:1;transform:translateY(0)}}@keyframes fadeUp {0%{opacity:0;transform:translateY(22px)}100%{opacity:1;transform:translateY(0)}}@keyframes marquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}@keyframes scrollBounce{0%{opacity:0.6;transform:translateY(0)}50%{opacity:1;transform:translateY(6px)}100%{opacity:0.6;transform:translateY(0)}}`;

if (typeof document !== 'undefined' && !document.getElementById('lp-keyframes')) {
  const style = document.createElement('style');
  style.id = 'lp-keyframes';
  style.innerHTML = keyframes;
  document.head.appendChild(style);
}

const LandingPage: React.FC = () => {
  const screen = useScreenSize();
  const { colorScheme, fontScheme } = useStyle();
  const isCompactMobile = screen.isMobile && screen.width < 390;
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const [isHowItWorksVisible, setIsHowItWorksVisible] = useState(false);
  const howItWorksRef = useRef<HTMLDivElement | null>(null);

  // Typing animation for hero title
  const fullTitle = 'Your DMs Shouldn\'t Be Your Booking System.';
  const [typedTitle, setTypedTitle] = useState('');
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTypedTitle(fullTitle.slice(0, i));
      if (i >= fullTitle.length) clearInterval(id);
    }, 60);
    return () => clearInterval(id);
  }, []);

  const sectionMaxWidth = screen.isMobile ? '100%' : '1050px';
  const padX = screen.isMobile ? (isCompactMobile ? '0.7rem' : '0.8rem') : '2.4rem';

  // Reveal-on-scroll for benefit cards
  useEffect(() => {
    trackViewContent('LoviBook Landing Page');
    trackTikTokViewContent('LoviBook Landing Page');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger how-it-works animations on scroll
  useEffect(() => {
    if (typeof window === 'undefined' || !howItWorksRef.current) return;
    if (!('IntersectionObserver' in window)) { setIsHowItWorksVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) { setIsHowItWorksVisible(true); io.unobserve(e.target); }
      });
    }, { threshold: 0.15 });
    io.observe(howItWorksRef.current);
    return () => io.disconnect();
  }, []);

  return (
     <PageLayout maxWidth="1240px">
       <style>{`
         @keyframes float1 {
           0%, 100% { transform: translateY(0px); }
           50% { transform: translateY(-30px); }
         }
         @keyframes float2 {
           0%, 100% { transform: translateY(0px); }
           50% { transform: translateY(-30px); }
         }
         @media (min-width: 1024px) {
           @keyframes float1 {
             0%, 100% { transform: translateY(0px); }
             50% { transform: translateY(-50px); }
           }
           @keyframes float2 {
             0%, 100% { transform: translateY(0px); }
             50% { transform: translateY(-50px); }
           }
         }
       `}</style>

       <div
         style={{
           fontFamily: fontScheme.BODY,
           background: `radial-gradient(circle at top, ${colorScheme.SECONDARY}55 0%, ${colorScheme.WHITE} 28%, ${colorScheme.BACKGROUND} 100%)`,
           minHeight: '100vh',
           width: '100%',
         }}
       >
      {/* Hero */}
      <section
        style={{
          width: '100%',
          maxWidth: sectionMaxWidth,
          margin: `0 auto ${screen.isMobile ? '0.6rem' : '0.8rem'}`,
          padding: screen.isMobile
            ? `${isCompactMobile ? '0.5rem' : '0.6rem'} ${padX} ${isCompactMobile ? '0.5rem' : '0.6rem'}`
            : `0.6rem ${padX} 1.2rem`,
          textAlign: screen.isMobile ? 'center' : 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: screen.isMobile ? (isCompactMobile ? '0.4rem' : '0.5rem') : '0.8rem',
          position: 'relative',
        }}
      >
        <div
          style={{
          alignSelf: 'center',
          padding: screen.isMobile
            ? (isCompactMobile ? '0.3rem 0.56rem' : '0.32rem 0.62rem')
            : '0.38rem 0.8rem',
          borderRadius: '999px',
          background: `${colorScheme.PRIMARY}12`,
          fontSize: screen.isMobile ? (isCompactMobile ? '0.52rem' : '0.56rem') : '0.68rem',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: colorScheme.PRIMARY,
          maxWidth: screen.isMobile ? '100%' : 'none',
          textAlign: 'center',
          display: 'none',
        }}
        >
        Appointment scheduling software
        </div>
        <h1 style={{ fontFamily: fontScheme.HEADING, fontSize: screen.isMobile ? (isCompactMobile ? '1.1rem' : '1.4rem') : '2.3rem', fontWeight: 700, lineHeight: screen.isMobile ? (isCompactMobile ? 1.1 : 1.15) : 1.2, color: '#1F2B1C', margin: 0, maxWidth: 'none', textAlign: 'center', whiteSpace: isCompactMobile ? 'nowrap' : 'normal' }} aria-label={fullTitle}>
        Your DMs Shouldn&apos;t Be Your <span style={{ color: colorScheme.PRIMARY, textDecoration: 'underline', textDecorationColor: colorScheme.PRIMARY, textDecorationThickness: '3px', textUnderlineOffset: '4px' }}>Booking System</span>.
        <span style={{ display: 'inline-block', width: '1px', height: '1em', marginLeft: '4px', borderRight: '2px solid #1F2B1C', opacity: typedTitle.length < fullTitle.length ? 1 : 0 }} />
        </h1>
        <p style={{ fontSize: screen.isMobile ? '0.9rem' : '1.15rem', maxWidth: screen.isMobile ? '100%' : '800px', margin: '0.5rem auto 0', lineHeight: 1.5, fontWeight: 500, color: colorScheme.TEXT_SECONDARY, textAlign: 'center' }}>
        Give clients one link to see your availability and book themselves — without the endless back-and-forth.
        </p>
        
        {/* Hero CTA */}
        <a href="/signup" onClick={(event) => { event.preventDefault(); navigateToSignupWithTracking(); }} style={{
          background: `linear-gradient(135deg, ${colorScheme.PRIMARY} 0%, ${colorScheme.ACCENT} 100%)`,
          color: '#FFF',
          padding: screen.isMobile ? '0.72rem 1.2rem' : '0.92rem 1.8rem',
          borderRadius: screen.isMobile ? '0.75rem' : '0.85rem',
          fontSize: screen.isMobile ? '0.88rem' : '0.95rem',
          fontWeight: 700,
          textDecoration: 'none',
          boxShadow: `0 10px 24px ${colorScheme.PRIMARY}20`,
          border: 'none',
          textAlign: 'center',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          cursor: 'pointer',
          marginTop: screen.isMobile ? '0.4rem' : '0.6rem',
          display: 'inline-block',
        }}>Create My Booking Page</a>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.3rem',
          fontSize: screen.isMobile ? '0.76rem' : '0.82rem',
          color: colorScheme.TEXT_SECONDARY,
          textAlign: 'center',
        }}>
          Already have an account?
          <a href="/login" style={{
            color: colorScheme.PRIMARY,
            fontWeight: 700,
            textDecoration: 'none',
          }}>
            Log in
          </a>
        </div>

        {/* Subtext under CTA */}
        <div style={{ fontSize: screen.isMobile ? '0.72rem' : '0.78rem', color: colorScheme.TEXT_SECONDARY, textAlign: 'center' }}>
          No hidden fees · Cancel anytime
        </div>

      </section>

      {/* Trust signals — infinite marquee */}
      <section style={{ padding: '0', borderTop: `1px solid ${colorScheme.BORDER}`, borderBottom: `1px solid ${colorScheme.BORDER}`, background: `linear-gradient(135deg, ${colorScheme.WHITE} 0%, ${colorScheme.BACKGROUND} 100%)`, position: 'relative', width: '100%', marginBottom: screen.isTablet ? '4.5rem' : 0 }}>
        <div style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', width: 'max-content', animation: 'marquee 18s linear infinite' }}>
            {[...TRUST_SIGNALS, ...TRUST_SIGNALS].map((label, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '1.2rem', padding: `${screen.isMobile ? '0.7rem' : '0.85rem'} 1.5rem` }}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
                  <circle cx="7" cy="7" r="7" fill={colorScheme.PRIMARY} fillOpacity="0.12" />
                  <polyline points="3.5,7 6,9.5 10.5,4.5" stroke={colorScheme.PRIMARY} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                </svg>
                <span style={{ fontSize: screen.isMobile ? '0.8rem' : '0.87rem', fontWeight: 600, color: colorScheme.TEXT_SECONDARY, whiteSpace: 'nowrap' }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Tablet-only scroll cue arrow — below the border */}
        {screen.isTablet && (
          <div style={{
            position: 'absolute',
            bottom: '-3.8rem',
            left: 0,
            right: 0,
            margin: '0 auto',
            width: 'fit-content',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '0.4rem',
            animation: 'scrollBounce 2s ease-in-out infinite',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}>
            <span style={{
              fontSize: '0.72rem',
              fontWeight: 600,
              color: colorScheme.TEXT_SECONDARY,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              opacity: 0.7,
            }}>Explore</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{
              stroke: colorScheme.PRIMARY,
              strokeWidth: 2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
              opacity: 0.8,
            }}>
              <polyline points="12 5 12 19 5 12"></polyline>
              <polyline points="12 5 12 19 19 12"></polyline>
            </svg>
          </div>
        )}
      </section>

      {/* Product Preview - Live Booking Demo */}
      <section style={{ maxWidth: sectionMaxWidth, margin: '0 auto', padding: `${screen.isMobile ? '1.5rem' : '2.5rem'} ${padX} ${screen.isMobile ? '0.8rem' : '1.2rem'}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: screen.isMobile ? '1rem' : '1.4rem' }}>
        {/* Caption */}
        <div style={{ fontSize: screen.isMobile ? '1.1rem' : '1.25rem', color: colorScheme.TEXT_PRIMARY, fontWeight: 600, textAlign: 'center', maxWidth: '520px' }}>
          Your booking page. Ready to share.
        </div>

        <div style={{
          width: screen.isMobile ? 'calc(100vw - 1.5rem)' : '720px',
          maxWidth: screen.isMobile ? 'none' : '100%',
          marginLeft: screen.isMobile ? 'calc(-50vw + 50% + 0.75rem)' : '0',
          marginRight: screen.isMobile ? 'calc(-50vw + 50% + 0.75rem)' : '0',
          animation: 'fadeSlide 0.7s ease both',
          position: 'relative',
        }}>
          <LandingPageDemo />
        </div>

        {/* Pricing & CTA beneath */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: screen.isMobile ? '0.4rem' : '0.5rem', alignItems: 'center' }}>
          <div style={{ fontSize: screen.isMobile ? '0.82rem' : '0.9rem', fontWeight: 700, color: colorScheme.TEXT_PRIMARY }}>
            $9.99/mo flat · No booking fees · No commissions
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section style={{ maxWidth: sectionMaxWidth, margin: '0 auto', padding: `${screen.isMobile ? '1rem' : '1.6rem'} ${padX}`, display: 'flex', flexDirection: 'column', gap: screen.isMobile ? '1rem' : '1.2rem', textAlign: 'center' }}>
        {/* Main benefit headline */}
        <h2 style={{
          fontSize: screen.isMobile ? '1.15rem' : '1.3rem',
          fontWeight: 700,
          lineHeight: 1.4,
          color: colorScheme.TEXT_PRIMARY,
          margin: 0,
          marginBottom: '0.3rem',
        }}>
          From &quot;Are you free Friday?&quot; to booked.
        </h2>

        {/* Supporting text */}
        <p style={{
          fontSize: screen.isMobile ? '0.85rem' : '0.9rem',
          fontWeight: 500,
          lineHeight: 1.5,
          color: colorScheme.TEXT_SECONDARY,
          margin: 0,
          maxWidth: '540px',
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          Clients choose a service, pick an available time, and book — all from one link.
        </p>

        {/* 3 benefits grid with SVG icons */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: screen.isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: screen.isMobile ? '0.9rem' : '1.2rem',
          maxWidth: '700px',
          marginLeft: 'auto',
          marginRight: 'auto',
          width: '100%',
          marginTop: screen.isMobile ? '0.5rem' : '0.8rem',
        }}>
          {[
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colorScheme.PRIMARY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              ),
              title: 'Share one link',
              desc: 'Add it to your Instagram bio.'
            },
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colorScheme.PRIMARY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              ),
              title: 'Clients book themselves',
              desc: 'No more availability back-and-forth.'
            },
            {
              icon: (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={colorScheme.PRIMARY} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              ),
              title: 'Stay organized',
              desc: 'Every appointment in one place.'
            },
          ].map((benefit, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.4rem',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                {benefit.icon}
              </div>
              <div style={{
                fontSize: screen.isMobile ? '0.85rem' : '0.88rem',
                fontWeight: 700,
                color: colorScheme.TEXT_PRIMARY,
              }}>
                {benefit.title}
              </div>
              <div style={{
                fontSize: screen.isMobile ? '0.73rem' : '0.76rem',
                color: colorScheme.TEXT_SECONDARY,
                lineHeight: 1.4,
              }}>
                {benefit.desc}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="plans" style={{ maxWidth: sectionMaxWidth, margin: '0 auto', padding: `${screen.isMobile ? '1.2rem' : '2.6rem'} ${padX}`, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: screen.isMobile ? '0.9rem' : '1.8rem', border: `2px solid ${colorScheme.PRIMARY}40`, borderRadius: '1.5rem', background: `linear-gradient(135deg, ${colorScheme.PRIMARY}1F 0%, ${colorScheme.SECONDARY}24 100%)`, marginTop: screen.isMobile ? '0.9rem' : '1.2rem', position: 'relative' }}>
        {/* Plan and market-context cards */}
        <div style={{ display: 'grid', gridTemplateColumns: screen.isMobile ? '1fr' : screen.isTablet ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))', gap: screen.isMobile ? '0.9rem' : '1.15rem', width: '100%', maxWidth: '980px', alignItems: 'stretch' }}>
          <div style={{ background: colorScheme.BACKGROUND, border: `1px solid ${colorScheme.PRIMARY}80`, borderRadius: '1.2rem', padding: screen.isMobile ? '1rem' : '1.45rem', display: 'flex', flexDirection: 'column', gap: screen.isMobile ? '0.7rem' : '0.9rem', boxShadow: `0 8px 20px ${colorScheme.PRIMARY}10`, position: 'relative' }}>
            <div style={{ fontSize: screen.isMobile ? '0.7rem' : '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colorScheme.TEXT_PRIMARY }}>LoviBook Basic</div>
            <div style={{ fontSize: screen.isMobile ? '1.8rem' : '2rem', fontWeight: 800, color: colorScheme.TEXT_PRIMARY, lineHeight: 1 }}>$9.99<span style={{ fontSize: '0.8rem', fontWeight: 600, marginLeft: '0.3rem' }}>/mo flat</span></div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
              {['Your own booking page', 'Online scheduling', 'Appointment reminders', 'Client reschedule & cancel', 'Manage breaks & time off'].map((item, i) => (
                <li key={i} style={{ fontSize: screen.isMobile ? '0.78rem' : '0.8rem', color: colorScheme.TEXT_PRIMARY, fontWeight: 700, display: 'flex', alignItems: 'flex-start', gap: '0.55rem' }}>
                  <span style={{ display: 'inline-block', width: '4px', height: '4px', background: colorScheme.PRIMARY, borderRadius: '50%', flexShrink: 0, marginTop: '0.65rem' }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <a href="/signup" onClick={(event) => { event.preventDefault(); navigateToSignupWithTracking(); }} style={{
              background: `linear-gradient(135deg, ${colorScheme.PRIMARY} 0%, ${colorScheme.ACCENT} 100%)`,
              color:'#FFF',
              padding: screen.isMobile ? '0.68rem 1rem' : '0.85rem 1.4rem',
              borderRadius: screen.isMobile ? '0.75rem' : '0.85rem',
              fontSize: screen.isMobile ? '0.82rem' : '0.85rem',
              fontWeight:700,
              textDecoration:'none',
              boxShadow:'0 8px 18px rgba(0,0,0,0.1)',
              border: `1px solid ${colorScheme.BORDER}`,
              textAlign: 'center',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              marginTop: 'auto',
            }}>Start with Basic</a>
          </div>

          <div style={{ background: colorScheme.BACKGROUND, border: `2px solid ${colorScheme.PRIMARY}`, borderRadius: '1.2rem', padding: screen.isMobile ? '1rem' : '1.45rem', display: 'flex', flexDirection: 'column', gap: screen.isMobile ? '0.7rem' : '0.9rem', boxShadow: `0 14px 30px ${colorScheme.PRIMARY}20`, position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-0.85rem', left: '50%', transform: 'translateX(-50%)', background: colorScheme.PRIMARY, color: colorScheme.WHITE, padding: '0.3rem 0.8rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                Best value
              </div>
              <div style={{ fontSize: screen.isMobile ? '0.7rem' : '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: colorScheme.PRIMARY }}>LoviBook Standard</div>
              <div style={{ fontSize: screen.isMobile ? '1.8rem' : '2rem', fontWeight: 800, color: colorScheme.TEXT_PRIMARY, lineHeight: 1 }}>$15.99<span style={{ fontSize: '0.8rem', fontWeight: 600, marginLeft: '0.3rem' }}>/mo flat</span></div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                {['Everything in Basic', 'Appointment deposits at booking', 'No-show protection', 'Provider-controlled deposit refunds', 'Premium fonts & colors'].map((item, i) => (
                  <li key={i} style={{ fontSize: screen.isMobile ? '0.78rem' : '0.8rem', color: colorScheme.TEXT_PRIMARY, fontWeight: 700, display: 'flex', alignItems: 'flex-start', gap: '0.55rem' }}>
                    <span style={{ display: 'inline-block', width: '4px', height: '4px', background: colorScheme.PRIMARY, borderRadius: '50%', flexShrink: 0, marginTop: '0.65rem' }} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href="/signup" onClick={(event) => { event.preventDefault(); navigateToSignupWithTracking(); }} style={{
                background: `linear-gradient(135deg, ${colorScheme.PRIMARY} 0%, ${colorScheme.ACCENT} 100%)`,
                color:'#FFF',
                padding: screen.isMobile ? '0.68rem 1rem' : '0.85rem 1.4rem',
                borderRadius: screen.isMobile ? '0.75rem' : '0.85rem',
                fontSize: screen.isMobile ? '0.82rem' : '0.85rem',
                fontWeight:700,
                textDecoration:'none',
                boxShadow:'0 8px 18px rgba(0,0,0,0.1)',
                border: `1px solid ${colorScheme.BORDER}`,
                textAlign: 'center',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                marginTop: 'auto',
              }}>Choose Standard</a>
          </div>

          {/* Typical software card */}
          <div style={{ background: `${colorScheme.BACKGROUND}`, border: `1px solid ${colorScheme.BORDER}`, borderRadius: '1.2rem', padding: screen.isMobile ? '1rem' : '1.45rem', display: 'flex', flexDirection: 'column', gap: screen.isMobile ? '0.7rem' : '0.9rem', opacity: 0.72, gridColumn: screen.isTablet ? '1 / -1' : undefined }}>
            <div style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: colorScheme.TEXT_SECONDARY }}>Typical booking platforms</div>
            <div style={{ fontSize: screen.isMobile ? '1.5rem' : '1.8rem', fontWeight: 800, color: colorScheme.TEXT_PRIMARY, lineHeight: 1 }}>$20–$30<span style={{ fontSize: '0.75rem', fontWeight: 600, marginLeft: '0.3rem' }}>/mo</span></div>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              {['Per-booking transaction fees', 'Referral commissions', 'Per-location upsells', 'Feature paywalls'].map((item, i) => (
                <li key={i} style={{ fontSize: '0.76rem', color: colorScheme.TEXT_SECONDARY, display: 'flex', alignItems: 'flex-start', gap: '0.55rem' }}>
                  <span style={{ display: 'inline-block', width: '16px', height: '1.5px', background: colorScheme.TEXT_SECONDARY, opacity: 0.4, flexShrink: 0, marginTop: '0.45rem' }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="how-it-works" ref={howItWorksRef} style={{ maxWidth: sectionMaxWidth, margin: '0 auto', padding: `${screen.isMobile ? '2rem' : '3rem'} ${padX} ${screen.isMobile ? '1.8rem' : '2.2rem'}`, display: 'flex', flexDirection: 'column', gap: screen.isMobile ? '1.2rem' : '1.8rem', position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: screen.isMobile ? '0.5rem' : '1rem',
            right: screen.isMobile ? '-0.5rem' : '-1rem',
            width: screen.isMobile ? '6rem' : '9rem',
            height: screen.isMobile ? '6rem' : '9rem',
            borderRadius: '50%',
            background: `${colorScheme.ACCENT}25`,
            filter: 'blur(8px)',
            animation: isHowItWorksVisible ? 'float1 4s ease-in-out infinite' : 'none',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: screen.isMobile ? '-1rem' : '0rem',
            left: screen.isMobile ? '-0.5rem' : '-1rem',
            width: screen.isMobile ? '5rem' : '8rem',
            height: screen.isMobile ? '5rem' : '8rem',
            borderRadius: '50%',
            background: `${colorScheme.PRIMARY}20`,
            filter: 'blur(8px)',
            animation: isHowItWorksVisible ? 'float2 5s ease-in-out infinite' : 'none',
            zIndex: 0,
            pointerEvents: 'none',
          }}
        />
        <div style={{ position: 'relative', zIndex: 1 }}>
        <h2 style={{ fontFamily: fontScheme.HEADING, fontSize: screen.isMobile ? '1.15rem' : '1.6rem', fontWeight: 700, color: colorScheme.PRIMARY, textAlign: 'center', margin: 0 }}>How LoviBook works</h2>
        </div>
        <p style={{ textAlign: 'center', fontSize: screen.isMobile ? '0.78rem' : '0.92rem', maxWidth: '760px', margin: '0 auto', lineHeight: 1.45, color: colorScheme.TEXT_SECONDARY }}>
          Set up once, share your link, and keep every appointment organized.
        </p>
        {/* Timeline */}
        <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: screen.isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))', gap: screen.isMobile ? '0' : '1.5rem' }}>
          {/* Connector line — desktop only */}
          {!screen.isMobile && (
            <div style={{ position: 'absolute', top: '1.35rem', left: 'calc(16.66% + 1rem)', right: 'calc(16.66% + 1rem)', height: '2px', background: `linear-gradient(90deg, ${colorScheme.ACCENT}60 0%, ${colorScheme.PRIMARY}60 100%)`, zIndex: 0 }} />
          )}
          {HOW_IT_WORKS.map((item, index) => (
            <div
              key={item.step}
              style={{
                display: 'flex',
                flexDirection: screen.isMobile ? 'row' : 'column',
                alignItems: screen.isMobile ? 'flex-start' : 'center',
                gap: screen.isMobile ? '0.85rem' : '0.7rem',
                position: 'relative',
                zIndex: 1,
                paddingBottom: screen.isMobile && index < HOW_IT_WORKS.length - 1 ? '1.5rem' : '0',
                animation: isHowItWorksVisible ? `fadeUp 0.55s ease ${index * 0.15}s both` : 'none',
              }}
            >
              {/* Step circle */}
              <div style={{
                flexShrink: 0,
                width: screen.isMobile ? '2.4rem' : '2.7rem',
                height: screen.isMobile ? '2.4rem' : '2.7rem',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${colorScheme.PRIMARY} 0%, ${colorScheme.ACCENT} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: screen.isMobile ? '0.8rem' : '0.9rem',
                fontWeight: 900,
                color: '#FFF',
                boxShadow: `0 6px 16px ${colorScheme.PRIMARY}40`,
              }}>{item.step}</div>
              {/* Mobile vertical connector */}
              {screen.isMobile && index < HOW_IT_WORKS.length - 1 && (
                <div style={{ position: 'absolute', left: '1.15rem', top: '2.4rem', bottom: '-0.1rem', width: '2px', background: `linear-gradient(180deg, ${colorScheme.ACCENT}50 0%, transparent 100%)` }} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', textAlign: screen.isMobile ? 'left' : 'center' }}>
                <div style={{ fontSize: screen.isMobile ? '0.88rem' : '0.92rem', fontWeight: 800, color: colorScheme.PRIMARY, lineHeight: 1.2 }}>{item.title}</div>
                <div style={{ fontSize: screen.isMobile ? '0.72rem' : '0.76rem', lineHeight: 1.5, color: colorScheme.TEXT_SECONDARY }}>{item.description}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section style={{ maxWidth: sectionMaxWidth, margin: '0 auto', padding: `${screen.isMobile ? '1.4rem' : '2rem'} ${padX} ${screen.isMobile ? '1.8rem' : '2.4rem'}` }}>
        <div
          style={{
            maxWidth: '760px',
            margin: '0 auto',
            padding: screen.isMobile ? '1.1rem 1rem' : '1.4rem 1.6rem',
            border: `1px solid ${colorScheme.BORDER}`,
            borderRadius: screen.isMobile ? '1rem' : '1.15rem',
            background: `linear-gradient(135deg, ${colorScheme.WHITE} 0%, ${colorScheme.BACKGROUND} 100%)`,
            boxShadow: '0 14px 28px rgba(0,0,0,0.05)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: screen.isMobile ? '0.8rem' : '1rem',
            textAlign: 'center',
          }}
        >
          <h2 style={{ fontFamily: fontScheme.HEADING, fontSize: screen.isMobile ? '1.15rem' : '1.55rem', fontWeight: 700, color: colorScheme.PRIMARY, margin: 0 }}>
            Built for small businesses.
          </h2>
          <p style={{ fontSize: screen.isMobile ? '0.78rem' : '0.88rem', color: colorScheme.TEXT_SECONDARY, margin: 0, maxWidth: '600px', lineHeight: 1.5 }}>
            You don&apos;t need hundreds of features or complicated pricing just to let clients book you online. LoviBook keeps it simple: $9.99/month.
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: screen.isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
              gap: screen.isMobile ? '0.55rem' : '0.8rem',
              width: '100%',
            }}
          >
            {[
              'Everything you need to start booking',
              'No booking fees or commissions',
              'Cancel anytime',
            ].map((item) => (
              <div
                key={item}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: screen.isMobile ? 'flex-start' : 'center',
                  gap: '0.45rem',
                  color: colorScheme.TEXT_PRIMARY,
                  fontSize: screen.isMobile ? '0.76rem' : '0.8rem',
                  fontWeight: 700,
                }}
              >
                <span style={{ color: colorScheme.PRIMARY, fontSize: '0.95rem' }} aria-hidden="true">&#10003;</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ maxWidth: sectionMaxWidth, margin: '0 auto', padding: `${screen.isMobile ? '2rem' : '3rem'} ${padX} 2.6rem`, display: 'flex', flexDirection: 'column', gap: screen.isMobile ? '1rem' : '1.25rem' }}>
        <h2 style={{ fontFamily: fontScheme.HEADING, fontSize: screen.isMobile ? '1.15rem' : '1.6rem', fontWeight: 700, color: colorScheme.PRIMARY, textAlign: 'center', margin: 0 }}>Frequently asked questions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '860px', width: '100%', margin: '0 auto' }}>
          {FAQ_ITEMS.map((item, index) => {
            const isOpen = openFaqIndex === index;
            return (
              <div
                key={item.question}
                style={{
                  background: `linear-gradient(180deg, ${colorScheme.WHITE} 0%, ${colorScheme.BACKGROUND} 100%)`,
                  border: `1px solid ${isOpen ? colorScheme.PRIMARY + '55' : colorScheme.BORDER}`,
                  borderRadius: '0.85rem',
                  overflow: 'hidden',
                  boxShadow: isOpen ? `0 8px 20px ${colorScheme.PRIMARY}12` : '0 4px 12px rgba(0,0,0,0.03)',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    padding: screen.isMobile ? '0.85rem 1rem' : '0.9rem 1.1rem',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                  aria-expanded={isOpen}
                >
                  <span style={{ fontSize: screen.isMobile ? '0.84rem' : '0.9rem', fontWeight: 700, color: colorScheme.PRIMARY, lineHeight: 1.35 }}>{item.question}</span>
                  <span style={{ flexShrink: 0, width: '18px', height: '18px', borderRadius: '50%', background: `${colorScheme.PRIMARY}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: colorScheme.PRIMARY, fontWeight: 700, transform: isOpen ? 'rotate(45deg)' : 'none', transition: 'transform 0.2s ease' }}>+</span>
                </button>
                {isOpen && (
                  <div style={{ padding: `0 1.1rem ${screen.isMobile ? '0.85rem' : '0.9rem'}`, fontSize: screen.isMobile ? '0.75rem' : '0.82rem', lineHeight: 1.6, color: colorScheme.TEXT_PRIMARY }}>
                    {item.answer}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section style={{ maxWidth: sectionMaxWidth, margin: '0 auto', padding: `0 ${padX} 3.2rem`, display: 'flex', justifyContent: 'center' }}>
        <div
          style={{
            width: '100%',
            maxWidth: '860px',
            background: `linear-gradient(135deg, ${colorScheme.PRIMARY} 0%, ${colorScheme.ACCENT} 100%)`,
            borderRadius: screen.isMobile ? '1.15rem' : '1.35rem',
            padding: screen.isMobile ? '1rem 0.95rem 1.05rem' : '1.3rem 1.45rem',
            color: colorScheme.WHITE,
            boxShadow: '0 22px 42px rgba(0,0,0,0.12)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: screen.isMobile ? '0.65rem' : '0.8rem',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: screen.isMobile ? '1fr' : 'minmax(0, 230px) minmax(0, 1fr)',
              alignItems: 'center',
              gap: screen.isMobile ? '0.85rem' : '1.3rem',
              width: '100%',
            }}
          >
            <div
              style={{
                background: 'rgba(255,255,255,0.14)',
                border: '1px solid rgba(255,255,255,0.22)',
                borderRadius: screen.isMobile ? '1rem' : '1.1rem',
                padding: screen.isMobile ? '0.55rem 0.65rem 0.45rem' : '0.7rem 0.75rem 0.55rem',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img
                src={loviLaptop}
                alt="Lovi working inside LoviBook"
                width={141}
                height={163}
                loading="lazy"
                style={{ width: '100%', maxWidth: screen.isMobile ? '5.5rem' : '6.5rem', height: 'auto', display: 'block' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: screen.isMobile ? '0.8rem' : '1rem', textAlign: 'center' }}>
              <div style={{ fontFamily: fontScheme.HEADING, fontSize: screen.isMobile ? '1.06rem' : '1.45rem', fontWeight: 800, lineHeight: 1.18 }}>
                Ready to get booking out of your DMs?
              </div>
              <div style={{ fontSize: screen.isMobile ? '0.74rem' : '0.86rem', lineHeight: 1.42, maxWidth: '38rem' }}>
                Create your booking page, share your link, and let clients book themselves.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: screen.isMobile ? '100%' : 'auto', maxWidth: screen.isMobile ? '21rem' : 'none' }}>
                  <a
                    href="/signup"
                    onClick={(event) => { event.preventDefault(); navigateToSignupWithTracking(); }}
                    style={{
                      background: colorScheme.WHITE,
                      color: colorScheme.PRIMARY,
                      textDecoration: 'none',
                      padding: screen.isMobile ? '0.78rem 0.95rem' : '0.72rem 1.05rem',
                      borderRadius: '0.9rem',
                      fontSize: screen.isMobile ? '0.82rem' : '0.86rem',
                      fontWeight: 800,
                      textAlign: 'center',
                      boxShadow: '0 10px 20px rgba(0,0,0,0.08)',
                    }}
                  >
                    Create My Booking Page
                  </a>
                  <span style={{ fontSize: screen.isMobile ? '0.68rem' : '0.72rem', opacity: 0.9 }}>
                    $9.99/month · Cancel anytime
                  </span>
                </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    </PageLayout>
  );
};

export default LandingPage;
